import { Trash2, GripVertical } from 'lucide-react';
import Button from '@/components/ui/Button';

// Compact FX note for table rows — just the rate + the flat 3% fee,
// highlighted, instead of spelling out the full base/fee/total arithmetic.
// Keyed off the stored fee (not currencyType !== 'SAR') so this stays
// correct regardless of the line owner's home currency — the fee is
// already baked into foreignFeeSar by the time it's saved. Falls back to
// the old SAR check for legacy rows saved before foreignFeeSar existed.
function fxNote(item) {
  const isForeign = item.foreignFeeSar != null
    ? item.foreignFeeSar > 0
    : !!item.currencyType && item.currencyType !== 'SAR';
  if (!isForeign) return null;
  const rate = Number(item.exchangeRate || 1);
  return `@ ${rate.toFixed(4)} · +3%`;
}

export default function LineItemsTable({ items, onRemove, readOnly = false }) {
  if (!items || items.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">
        No line items yet. Add expenses manually or use the AI receipt scanner.
      </div>
    );
  }

  const totalSAR = items.reduce((sum, item) => sum + (item.sarAmount || 0), 0);
  // foreignFeeSar is persisted per line (3% of the SAR-converted amount for
  // non-SAR currencies, 0 for SAR). sarAmount already includes it — this is
  // just for the "incl. fee" transparency note below.
  const totalForeignFeeSAR = items.reduce((sum, item) => sum + (item.foreignFeeSar || 0), 0);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground uppercase tracking-wider">
            <th className="pb-3 pr-3 w-10">#</th>
            <th className="pb-3 pr-3">Date</th>
            <th className="pb-3 pr-3">Type</th>
            <th className="pb-3 pr-3">Description</th>
            <th className="pb-3 pr-3">Currency</th>
            <th className="pb-3 pr-3 text-right">Amount</th>
            <th className="pb-3 pr-3 text-right">Rate</th>
            <th className="pb-3 pr-3 text-right">SAR</th>
            {!readOnly && <th className="pb-3 w-10" />}
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr
              key={item.rowKey || i}
              className="border-b border-border/50 hover:bg-white/[0.02] transition-colors"
            >
              <td className="py-3 pr-3 text-muted-foreground">{i + 1}</td>
              <td className="py-3 pr-3 whitespace-nowrap">{item.date}</td>
              <td className="py-3 pr-3">
                <span className="inline-flex items-center rounded-md bg-muted/50 px-2 py-0.5 text-xs font-medium">
                  {item.expenseTypeName || item.expenseTypeId}
                </span>
              </td>
              <td className="py-3 pr-3 max-w-[200px] truncate" title={item.description}>
                {item.description || <span className="text-muted-foreground/50 italic">No description</span>}
              </td>
              <td className="py-3 pr-3 text-muted-foreground">{item.currencyType}</td>
              <td className="py-3 pr-3 text-right font-mono">
                {(item.currencyAmount || 0).toFixed(2)}
              </td>
              <td className="py-3 pr-3 text-right font-mono text-muted-foreground">
                {(item.exchangeRate || 1).toFixed(4)}
              </td>
              <td className="py-3 pr-3 text-right">
                <div className="font-mono font-semibold text-profun-cyan">
                  {(item.sarAmount || 0).toFixed(2)}
                </div>
                {fxNote(item) && (
                  <div className="mt-0.5 inline-flex items-center rounded-full bg-profun-cyan/10 px-1.5 py-0.5 text-[10px] font-medium text-profun-cyan whitespace-nowrap">
                    {fxNote(item)}
                  </div>
                )}
              </td>
              {!readOnly && (
                <td className="py-3">
                  <button
                    onClick={() => onRemove(i)}
                    className="p-1.5 rounded-lg text-muted-foreground hover:text-profun-pink hover:bg-profun-pink/10 transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
        <tfoot>
          {totalForeignFeeSAR > 0 && (
            <tr>
              <td colSpan={readOnly ? 7 : 7} className="pt-2 pr-3 text-right text-xs text-muted-foreground">
                Foreign txn fees (3%):
              </td>
              <td className="pt-2 pr-3 text-right font-mono text-xs text-muted-foreground">
                {totalForeignFeeSAR.toFixed(2)}
              </td>
              {!readOnly && <td />}
            </tr>
          )}
          <tr className="border-t border-border">
            <td colSpan={readOnly ? 7 : 7} className="py-3 pr-3 text-right font-semibold text-muted-foreground">
              Total (SAR):
            </td>
            <td className="py-3 pr-3 text-right font-mono font-bold text-lg text-profun-cyan">
              {totalSAR.toFixed(2)}
            </td>
            {!readOnly && <td />}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
